import throttle from 'lodash/throttle';
import { AnimatePresence, m } from 'motion/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  isOpen: boolean;
  targetRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}

/**
 * Simple tooltip component for desktop with throttled positioning updates
 */
const Tooltip: React.FC<TooltipProps> = ({
  children,
  isOpen,
  targetRef,
  onClose,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const updatePosition = (): void => {
      if (targetRef.current && tooltipRef.current) {
        const targetRect = targetRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top = targetRect.top - tooltipRect.height - 16;

        let left =
          targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;

        const padding = 16;
        left = Math.max(
          padding,
          Math.min(left, window.innerWidth - tooltipRect.width - padding)
        );

        top = Math.max(padding, top);

        setPosition({ top, left });
      }
    };

    // Throttle the positioning updates to improve performance
    const throttledUpdatePosition = throttle(updatePosition, 16);

    if (isOpen) {
      updatePosition();

      window.addEventListener('resize', throttledUpdatePosition);
      window.addEventListener('scroll', throttledUpdatePosition);
    }

    return () => {
      window.removeEventListener('resize', throttledUpdatePosition);
      window.removeEventListener('scroll', throttledUpdatePosition);
      throttledUpdatePosition.cancel();
    };
  }, [isOpen, targetRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        targetRef.current &&
        !targetRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, targetRef]);

  useEffect(() => {
    const handleScroll = (): void => onClose();

    if (isOpen) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <m.div
          ref={tooltipRef}
          className="bg-background-default fixed z-50 w-80 rounded px-4 py-3"
          style={{
            top: position.top,
            left: position.left,
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          data-test="reviews-tooltip"
          role="tooltip"
          id="tooltip-content"
        >
          <div className="bg-background-default absolute -bottom-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45" />
          {children}
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default Tooltip;
